import Styles from './Default.module.scss'
import { ShareSheetItem } from '../../ShareSheetItem/ShareSheetItem'
import { AssetAccessGrantWithParent } from 'types'
import { getIcon } from 'components/ShareSheet/utils'
import { ChannelType } from 'components/CampaignCreationModal/Types'
import { Export, Facebook, Instagram, LinkedIn, Public, Twitter, Youtube } from 'almanac-ui/icons'
import { Button } from 'components/Button'
import { ReactNode } from 'react'
import { Access } from '../../Access/Access'
import { useModals } from 'almanac-ui/Modal'
import { ExportModal } from 'components/ExportModal/ExportModal'
import { ShareSheetScreen, useShareSheetContext } from '../../ShareSheetV2'
import useShareSheetV2 from 'hooks/useShareSheetV2'
import CircleCheck from 'almanac-ui/icons/16/CircleCheck'
import { DownloadItem } from './DownloadItem'
import { PostToSocialItem } from './PostToSocialItem'
import cx from 'classnames'
import { InformalFeedbackRequest } from 'api/clients/InformalFeedbackRequestsClient'
import useResponsiveDesign from 'hooks/useResponsiveDesign'
import { ResponsiveDesign } from 'screens/blaze/Home/components/ResponsiveDesign'
import { observer } from 'mobx-react-lite'
import { useRootStore } from 'stores/RootStore'
import { Heading } from 'blaze-ui/Heading/Heading'
import { DesignChannel } from 'components/NewFileModal/Types'
import { Modal } from 'almanac-ui/Modal'

interface Props {
  asset: AssetAccessGrantWithParent
  request?: InformalFeedbackRequest
}

export type SocialChannelType =
  | ChannelType.FacebookPost
  | ChannelType.InstagramPosts
  | ChannelType.InstagramReel
  | ChannelType.LinkedInPosts
  | ChannelType.Tweets
  | ChannelType.InstagramReel
  | ChannelType.TweetThread
  | ChannelType.BlogPost
  | DesignChannel.LinkedInPost
  | DesignChannel.InstagramPost
  | DesignChannel.InstagramStory
  | DesignChannel.TwitterPost
  | DesignChannel.FacebookPost
  | DesignChannel.YoutubeShort
  | DesignChannel.TikTokPost

export const socialChannels = [
  ChannelType.FacebookPost,
  ChannelType.InstagramPosts,
  ChannelType.LinkedInPosts,
  ChannelType.Tweets,
  ChannelType.InstagramReel,
  DesignChannel.LinkedInPost,
  DesignChannel.InstagramPost,
  DesignChannel.InstagramStory,
  DesignChannel.TwitterPost,
  DesignChannel.FacebookPost,
  DesignChannel.YoutubeShort,
  DesignChannel.TikTokPost,
]

export const platformIcons: Record<string, React.ReactNode> = {
  facebook: <Facebook size={24} />,
  instagram: <Instagram size={24} />,
  linkedin: <LinkedIn size={24} />,
  x: <Twitter size={24} />,
  youtube: <Youtube size={24} />,
}

export const platformNameFromChannel: Record<SocialChannelType | ChannelType.BlogPost, string> = {
  [ChannelType.FacebookPost]: 'Facebook',
  [ChannelType.InstagramPosts]: 'Instagram',
  [ChannelType.InstagramReel]: 'Instagram',
  [ChannelType.LinkedInPosts]: 'LinkedIn',
  [ChannelType.Tweets]: 'X',
  [ChannelType.TweetThread]: 'X',
  [ChannelType.BlogPost]: 'WordPress',
  [DesignChannel.LinkedInPost]: 'LinkedIn',
  [DesignChannel.InstagramPost]: 'Instagram',
  [DesignChannel.InstagramStory]: 'Instagram',
  [DesignChannel.TwitterPost]: 'X',
  [DesignChannel.YoutubeShort]: 'YouTube',
  [DesignChannel.TikTokPost]: 'TikTok',
}

export const platformKeyByChannel: Record<SocialChannelType, keyof typeof platformIcons> = {
  [ChannelType.FacebookPost]: 'facebook',
  [ChannelType.InstagramPosts]: 'instagram',
  [ChannelType.InstagramReel]: 'instagram',
  [ChannelType.LinkedInPosts]: 'linkedin',
  [ChannelType.Tweets]: 'x',
  [ChannelType.TweetThread]: 'x',
  [ChannelType.BlogPost]: 'word_press',
  [DesignChannel.LinkedInPost]: 'linkedin',
  [DesignChannel.InstagramPost]: 'instagram',
  [DesignChannel.InstagramStory]: 'instagram',
  [DesignChannel.TwitterPost]: 'x',
  [DesignChannel.YoutubeShort]: 'youtube',
  [DesignChannel.TikTokPost]: 'tiktok',
}

const ExportItem = ({
  fullWidth,
  halfWidth,
  action,
  description,
}: {
  fullWidth?: boolean
  halfWidth?: boolean
  action?: ReactNode
  description: string
}) => {
  const { file } = useShareSheetContext()
  const { openModal } = useModals()

  return (
    <ShareSheetItem
      icon={<Export size={24} />}
      title="Export"
      description={description}
      onClick={() => openModal(ExportModal, { articleId: file.id, showBackButton: true })}
      fullWidth={fullWidth}
      halfWidth={halfWidth}
      action={action}
    />
  )
}

const PublishToWebItem = ({ halfWidth }: { halfWidth?: boolean }) => {
  const { setScreen, file } = useShareSheetContext()
  const { publicAccess } = useShareSheetV2(file)

  return (
    <ShareSheetItem
      icon={<Public size={24} color={publicAccess ? 'var(--positive-50)' : undefined} />}
      title="Publish to Web"
      halfWidth={halfWidth}
      extra={publicAccess && <CircleCheck size={16} color={'var(--positive-50)'} />}
      description="share link with anyone"
      onClick={() => setScreen(ShareSheetScreen.PublicAccess)}
    />
  )
}
export const Default = observer(({ asset, request }: Props) => {
  const { type, header_emoji, title, channel } = asset
  const { file, isPublishableToWeb } = useShareSheetContext()
  const { openModal, closeModal } = useModals()
  const { isMobile } = useResponsiveDesign()
  const { drawersStore } = useRootStore()
  const isSocialPost = channel && socialChannels.includes(channel)

  const actions = (asset: AssetAccessGrantWithParent) => {
    if (isSocialPost && !isMobile) {
      return (
        <div className={Styles.content}>
          <PostToSocialItem asset={asset} />

          <div className={Styles.options}>
            {asset.type === 'article' && (
              <ExportItem
                halfWidth={!isPublishableToWeb && !isMobile}
                fullWidth={isPublishableToWeb || isMobile}
                description="Zapier, Social Media, etc."
              />
            )}
            <DownloadItem halfWidth />
            <PublishToWebItem halfWidth />
          </div>
        </div>
      )
    }

    return (
      <div className={cx(Styles.content, Styles.notSocialPost, { [Styles.design]: asset.type === 'design' })}>
        {asset.type === 'article' && (
          <ExportItem
            fullWidth={isPublishableToWeb || isMobile}
            halfWidth={!isPublishableToWeb && !isMobile}
            action={
              <Button
                size="lg"
                onPress={() => {
                  openModal(ExportModal, { articleId: file.id })
                  drawersStore.closeDrawer()
                }}
              >
                Export
              </Button>
            }
            description="Zapier, WordPress, Social Media etc."
          />
        )}
        <DownloadItem halfWidth />
        <PublishToWebItem halfWidth />
      </div>
    )
  }

  return (
    <>
      <div className={Styles.container}>
        <ResponsiveDesign.Desktop>
          <Modal.Header
            title={
              <div className={Styles.titleContainer}>
                <span className={Styles.emoji}>{getIcon(type, header_emoji, true, channel)}</span>
                <div className={Styles.title}>
                  <Heading level={4} lineClamp={1}>
                    {title}
                  </Heading>
                </div>
              </div>
            }
            onBack={closeModal}
          />
        </ResponsiveDesign.Desktop>
        {actions(asset)}
      </div>
      <Access request={request} />
    </>
  )
})
